import { Controller } from "@hotwired/stimulus"
import * as echarts from "echarts"

window.echarts = echarts

const TOOLTIP_STYLE = {
  confine: true,
  textStyle: { align: "left", color: "#444", fontSize: 13 },
  backgroundColor: "#FFFFFF",
  borderColor: "rgba(0, 0, 0, 0.1)",
  borderWidth: 1,
  extraCssText: "box-shadow: 0px 3px 5px rgba(0, 0, 0, 0.05);",
  trigger: "axis"
}

const AXIS_LABEL_STYLE = { fontSize: 11, color: "#767676" }

export default class extends Controller {
  static targets = [
    "globalAccuracy", "allocationTot", "quantityTot",
    "overallChart", "userChart", "producerChart",
    "userBarChart", "producerBarChart",
    "biomeSelect", "levelSelect", "yearSelect",
    "dataJson", "loading"
  ]

  static values = {
    initSlug: String,
    initId: Number,
    collection: String,
    biome: String,
    level: String,
    year: Number,
    legend: Object,
    i18n: Object
  }

  connect() {
    this.overallChartInstance = null
    this.userChartInstance = null
    this.producerChartInstance = null
    this.userBarChartInstance = null
    this.producerBarChartInstance = null

    this.normalizedLegend = this.normalizeLegend(this.legendValue)

    this.handleResize = this.handleResize.bind(this)
    window.addEventListener("resize", this.handleResize)

    this.renderAllCharts()
  }

  normalizeLegend(raw) {
    const result = {}
    for (const [key, val] of Object.entries(raw || {})) {
      result[key] = val
      result[parseInt(key, 10)] = val
    }
    return result
  }

  disconnect() {
    window.removeEventListener("resize", this.handleResize)
    this.disposeCharts()
  }

  handleResize() {
    [this.overallChartInstance, this.userChartInstance, this.producerChartInstance,
    this.userBarChartInstance, this.producerBarChartInstance].forEach(chart => {
      if (chart) chart.resize()
    })
  }

  disposeCharts() {
    [this.overallChartInstance, this.userChartInstance, this.producerChartInstance,
    this.userBarChartInstance, this.producerBarChartInstance].forEach(chart => {
      if (chart) chart.dispose()
    })
  }

  changeInitiative(event) {
    const slug = event.target.value
    if (slug) {
      window.location.href = `/${slug}`
    } else {
      window.location.href = `/`
    }
  }

  changeCollection(event) {
    this.collectionValue = event.target.value
    this.loadFiltersAndData()
  }

  changeFilter() {
    this.search()
  }

  changeYear() {
    const year = parseInt(this.yearSelectTarget.value)
    this.yearValue = year
    this.loadData()
  }

  search() {
    this.biomeValue = this.biomeSelectTarget.value
    this.levelValue = this.levelSelectTarget.value
    this.loadData()
  }

  showLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.remove("hidden")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden")
  }

  async loadFiltersAndData() {
    this.showLoading()
    const collection = this.collectionValue
    const initiativeId = this.initIdValue
    try {
      const resp = await fetch(`/api/accuracies/filters?collection=${collection}&initiative_id=${initiativeId}`)
      const data = await resp.json()

      this.updateBiomeSelect(data.biomes)
      this.updateLevelSelect(data.levels)
      this.updateYearSelect(data.years)

      this.biomeValue = data.biomes.includes(this.biomeValue) ? this.biomeValue : "Brasil"
      const filteredLevels = data.levels.filter(l => l !== "l3")
      this.levelValue = filteredLevels.includes(this.levelValue) ? this.levelValue : filteredLevels[0]

      this.loadData()
    } catch (e) {
      console.error("Error loading filters:", e)
    } finally {
      this.hideLoading()
    }
  }

  async loadData() {
    this.showLoading()
    const params = new URLSearchParams({
      collection: this.collectionValue,
      territory: this.biomeValue,
      level: this.levelValue,
      year: this.yearValue,
      initiative_id: this.initIdValue
    })

    try {
      const resp = await fetch(`/api/accuracies/data?${params}`)
      const data = await resp.json()
      this.updateResult(data)
      this.renderAllCharts()
    } catch (e) {
      console.error("Error loading data:", e)
    } finally {
      this.hideLoading()
    }
  }

  updateResult(data) {
    this.globalAccuracyTarget.textContent = this.formatCardPercent(data.global_accuracy)
    this.allocationTotTarget.textContent = this.formatCardPercent(data.allocation_tot)
    this.quantityTotTarget.textContent = this.formatCardPercent(data.quantity_tot)
    this.dataJsonTarget.textContent = JSON.stringify(data)
  }

  updateBiomeSelect(biomes) {
    this.biomeSelectTarget.innerHTML = biomes.map(b =>
      `<option value="${b}" ${b === this.biomeValue ? "selected" : ""}>${b}</option>`
    ).join("")
  }

  updateLevelSelect(levels) {
    const filtered = levels.filter(l => l !== "l3")
    this.levelSelectTarget.innerHTML = filtered.map(l =>
      `<option value="${l}" ${l === this.levelValue ? "selected" : ""}>Nível ${l.replace(/\D/, "")}</option>`
    ).join("")
  }

  updateYearSelect(years) {
    this.yearSelectTarget.innerHTML = years.map(y =>
      `<option value="${y}" ${y === this.yearValue ? "selected" : ""}>${y}</option>`
    ).join("")
    if (!years.includes(this.yearValue)) {
      this.yearValue = years[years.length - 1]
      this.yearSelectTarget.value = this.yearValue
    }
  }

  renderAllCharts() {
    const data = JSON.parse(this.dataJsonTarget.textContent || "{}")
    const legend = this.normalizedLegend

    this.renderOverallChart(data.overall_accuracy || [])
    this.renderUserChart(data.user_accuracy || [], legend)
    this.renderProducerChart(data.producer_accuracy || [], legend)
    this.renderUserBarChart(data.confusion_user_right || [], data.confusion_user_left || [], legend)
    this.renderProducerBarChart(data.confusion_prod_right || [], data.confusion_prod_left || [], legend)
  }

  renderOverallChart(accuracy) {
    if (!this.hasOverallChartTarget) return

    if (!this.overallChartInstance) {
      this.overallChartInstance = echarts.init(this.overallChartTarget)
    }

    const categories = accuracy.map(a => a.year)
    const values = accuracy.map(a => parseFloat(a.value.toFixed(1)))

    this.overallChartInstance.setOption({
      tooltip: { ...TOOLTIP_STYLE },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: false,
        axisPointer: { type: "shadow" },
        axisLabel: AXIS_LABEL_STYLE,
        axisLine: { color: "#DDD" }
      },
      yAxis: {
        type: "value",
        max: 100,
        axisTick: { show: false },
        axisLine: { color: "#DDD" },
        splitLine: { lineStyle: { color: "rgba(0, 0, 0, 0.05)" } },
        axisLabel: { ...AXIS_LABEL_STYLE }
      },
      series: [{
        name: this.i18nValue?.value || "Valor",
        type: "line",
        data: values,
        itemStyle: { color: "#00A00D" }
      }]
    }, { notMerge: true })
  }

  renderUserChart(accuracy, legend) {
    if (!this.hasUserChartTarget) return

    if (!this.userChartInstance) {
      this.userChartInstance = echarts.init(this.userChartTarget)
    }

    if (accuracy.length === 0) {
      this.userChartInstance.clear()
      return
    }

    const categories = accuracy[0].statistics.map(s => s.year)
    const series = accuracy.map(a => ({
      name: legend[a.class_id]?.default_name || a.class_id.toString(),
      type: "line",
      data: a.statistics.map(s => parseFloat(s.value.toFixed(1))),
      itemStyle: { color: legend[a.class_id]?.color }
    }))

    this.userChartInstance.setOption({
      tooltip: { ...TOOLTIP_STYLE },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: false,
        axisPointer: { type: "shadow" },
        axisLabel: AXIS_LABEL_STYLE,
        axisLine: { color: "#DDD" }
      },
      yAxis: {
        type: "value",
        max: 100,
        axisTick: { show: false },
        axisLine: { color: "#DDD" },
        splitLine: { lineStyle: { color: "rgba(0, 0, 0, 0.05)" } },
        axisLabel: { ...AXIS_LABEL_STYLE }
      },
      series: series
    }, { notMerge: true })
  }

  renderProducerChart(accuracy, legend) {
    if (!this.hasProducerChartTarget) return

    if (!this.producerChartInstance) {
      this.producerChartInstance = echarts.init(this.producerChartTarget)
    }

    if (accuracy.length === 0) {
      this.producerChartInstance.clear()
      return
    }

    const categories = accuracy[0].statistics.map(s => s.year)
    const series = accuracy.map(a => ({
      name: legend[a.class_id]?.default_name || a.class_id.toString(),
      type: "line",
      data: a.statistics.map(s => parseFloat(s.value.toFixed(1))),
      itemStyle: { color: legend[a.class_id]?.color }
    }))

    this.producerChartInstance.setOption({
      tooltip: { ...TOOLTIP_STYLE },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: false,
        axisPointer: { type: "shadow" },
        axisLabel: AXIS_LABEL_STYLE,
        axisLine: { color: "#DDD" }
      },
      yAxis: {
        type: "value",
        max: 100,
        axisTick: { show: false },
        axisLine: { color: "#DDD" },
        splitLine: { lineStyle: { color: "rgba(0, 0, 0, 0.05)" } },
        axisLabel: { ...AXIS_LABEL_STYLE }
      },
      series: series
    }, { notMerge: true })
  }

  renderUserBarChart(rightData, leftData, legend) {
    if (!this.hasUserBarChartTarget) return

    if (!this.userBarChartInstance) {
      this.userBarChartInstance = echarts.init(this.userBarChartTarget)
    }

    const filteredRight = rightData
      .filter(item => item.class_id !== -1)
      .sort((a, b) => a.class_id - b.class_id)

    if (filteredRight.length === 0) {
      this.userBarChartInstance.clear()
      return
    }

    const groupedLeft = this.groupConfusionLeft(leftData)
    const countLeftSeries = groupedLeft.length > 0 ? groupedLeft[0].data.length : 0

    const seriesData = [{
      type: "bar",
      stack: "classId",
      data: filteredRight.map(item => ({
        name: legend[item.class_id]?.default_name || item.class_id.toString(),
        value: parseFloat(item.value.toFixed(1)),
        itemStyle: { color: legend[item.class_id]?.color }
      }))
    }]

    for (let i = 0; i < countLeftSeries; i++) {
      seriesData.push({
        type: "bar",
        stack: "classId",
        data: groupedLeft.map(group => {
          const entry = group.data[i]
          if (!entry) return { name: "", value: 0, itemStyle: {} }
          return {
            name: legend[entry.id_class_map]?.default_name || entry.id_class_map.toString(),
            value: parseFloat((entry.value * -1).toFixed(1)),
            itemStyle: { color: legend[entry.id_class_map]?.color }
          }
        })
      })
    }

    const categories = seriesData[0].data.map(d => d.name)

    this.userBarChartInstance.setOption({
      tooltip: {
        ...TOOLTIP_STYLE,
        formatter: (params) => {
          let content = `<strong>${params[0].axisValue}</strong><br/>`
          content += `<div><i>Correto</i></div>`
          params.forEach(item => {
            if (item.value > 0) {
              content += `<div style="display: flex; justify-content: space-between;"><span style="margin-right: 5px;">${item.marker} ${item.data.name}</span><span>${item.value}%</span></div>`
            }
          })
          const errorsLabel = this.i18nValue?.errors || "Erros"
          content += `<div><i>${errorsLabel}</i></div>`
          params.forEach(item => {
            if (item.value < 0) {
              content += `<div style="display: flex; justify-content: space-between;"><span style="margin-right: 5px;">${item.marker} ${item.data.name}</span><span>${item.value}%</span></div>`
            }
          })
          return content
        }
      },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: [{ type: "value", min: -100, max: 100 }],
      yAxis: [{ type: "category", data: categories, axisTick: { show: false }, axisLabel: { ...AXIS_LABEL_STYLE, fontSize: 10, overflow: "truncate", width: 80, ellipsis: "…" } }],
      series: seriesData
    }, { notMerge: true })
  }

  renderProducerBarChart(rightData, leftData, legend) {
    if (!this.hasProducerBarChartTarget) return

    if (!this.producerBarChartInstance) {
      this.producerBarChartInstance = echarts.init(this.producerBarChartTarget)
    }

    const filteredRight = rightData
      .filter(item => item.class_id !== -1)
      .sort((a, b) => a.class_id - b.class_id)

    if (filteredRight.length === 0) {
      this.producerBarChartInstance.clear()
      return
    }

    const groupedLeft = this.groupConfusionLeft(leftData)
    const countLeftSeries = groupedLeft.length > 0 ? groupedLeft[0].data.length : 0

    const seriesData = [{
      type: "bar",
      stack: "classId",
      data: filteredRight.map(item => ({
        name: legend[item.class_id]?.default_name || item.class_id.toString(),
        value: parseFloat(item.value.toFixed(1)),
        itemStyle: { color: legend[item.class_id]?.color }
      }))
    }]

    for (let i = 0; i < countLeftSeries; i++) {
      seriesData.push({
        type: "bar",
        stack: "classId",
        data: groupedLeft.map(group => {
          const entry = group.data[i]
          if (!entry) return { name: "", value: 0, itemStyle: {} }
          return {
            name: legend[entry.id_class_map]?.default_name || entry.id_class_map.toString(),
            value: parseFloat((entry.value * -1).toFixed(1)),
            itemStyle: { color: legend[entry.id_class_map]?.color }
          }
        })
      })
    }

    const categories = seriesData[0].data.map(d => d.name)

    this.producerBarChartInstance.setOption({
      tooltip: {
        ...TOOLTIP_STYLE,
        formatter: (params) => {
          let content = `<strong>${params[0].axisValue}</strong><br/>`
          const correctLabel = this.i18nValue?.correct || "Correto"
          const errorsLabel = this.i18nValue?.errors || "Erros"
          content += `<div><i>${correctLabel}</i></div>`
          params.forEach(item => {
            if (item.value > 0) {
              content += `<div style="display: flex; justify-content: space-between;"><span style="margin-right: 5px;">${item.marker} ${item.data.name}</span><span>${item.value}%</span></div>`
            }
          })
          content += `<div><i>${errorsLabel}</i></div>`
          params.forEach(item => {
            if (item.value < 0) {
              content += `<div style="display: flex; justify-content: space-between;"><span style="margin-right: 5px;">${item.marker} ${item.data.name}</span><span>${item.value}%</span></div>`
            }
          })
          return content
        }
      },
      grid: { top: "8%", right: "20px", bottom: "8%", left: "20px", containLabel: true },
      xAxis: [{ type: "value", min: -100, max: 100 }],
      yAxis: [{ type: "category", data: categories, axisTick: { show: false }, axisLabel: { ...AXIS_LABEL_STYLE, fontSize: 10, overflow: "truncate", width: 80, ellipsis: "…" } }],
      series: seriesData
    }, { notMerge: true })
  }

  groupConfusionLeft(data) {
    const grouped = {}
    data.forEach(item => {
      const key = item.class_id
      if (!grouped[key]) grouped[key] = { classId: key, data: [] }
      grouped[key].data.push(item)
    })

    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .map(key => grouped[key])
  }

  formatCardPercent(value) {
    return `${(value || 0).toFixed(2)}%`
  }
}
